/**
 * Spec 086 — Fan-Out cluster detail page.
 *
 * Consultant-facing detail view for a single fan-out cluster. Renders
 * cluster metadata (query count, engine coverage, primary sub-query type)
 * and a filterable list of member queries.
 *
 * Phase 9 Session C gap 086-1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { useBrand } from '../../lib/brand-context.js';
import { apiGet } from '../../lib/api.js';
import { EmptyState } from '../../components/EmptyState.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import {
  getFanoutCluster,
  type FanoutClusterDetail,
  type FanoutQueryMember,
  type FanoutSourceConfidence,
} from '../../api/fanout.js';
import type {
  PromptTemplate,
  PromptTemplateListResponse,
} from '../../types/index.js';

/** Truncate to ~max chars with an ellipsis, on a word boundary when easy. */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}

/**
 * A synthesized member carries its seed prompt(s) inside `sources[]` as
 * `{ type: 'synthesized', seed_prompt_id, pattern }`. Returns the distinct
 * seed-prompt ids in order of first appearance (observed members yield []).
 */
function extractSeedPromptIds(member: FanoutQueryMember): string[] {
  const ids: string[] = [];
  for (const s of member.sources) {
    if (s && typeof s === 'object' && s.type === 'synthesized') {
      const raw = (s as { seed_prompt_id?: unknown }).seed_prompt_id;
      if (raw != null) {
        const id = String(raw);
        if (id && !ids.includes(id)) ids.push(id);
      }
    }
  }
  return ids;
}

const CONFIDENCE_STYLES: Record<FanoutSourceConfidence, string> = {
  observed: 'bg-emerald-100 text-emerald-800',
  inferred_high: 'bg-sky-100 text-sky-800',
  inferred_medium: 'bg-amber-100 text-amber-800',
  inferred_low: 'bg-slate-200 text-slate-700',
};

const CONFIDENCE_OPTIONS: readonly FanoutSourceConfidence[] = [
  'observed',
  'inferred_high',
  'inferred_medium',
  'inferred_low',
] as const;

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

export function FanOutClusterDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const brandId = selectedBrand?.id ?? null;

  const [detail, setDetail] = useState<FanoutClusterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  // id → template text, for resolving a synthesized member's source prompt.
  const [promptMap, setPromptMap] = useState<Map<string, string>>(
    () => new Map(),
  );

  const [confidenceFilter, setConfidenceFilter] = useState<
    FanoutSourceConfidence | ''
  >('');
  const [engineFilter, setEngineFilter] = useState('');

  const load = useCallback(async () => {
    if (!brandId || !slug) return;
    setLoading(true);
    setError(null);
    setNotFound(false);

    // Prompt templates resolve a synthesized member's seed_prompt_id → text.
    // Fetched alongside the cluster; failures are swallowed so members still
    // render (we just omit the source-prompt line).
    const tenantId = selectedBrand?.tenant_id;
    const promptsPromise: Promise<Map<string, string>> = tenantId
      ? apiGet<PromptTemplateListResponse>(
          `/api/v1/tenants/${tenantId}/prompts?limit=100&is_active=true&brand_id=${brandId}`,
        )
          .then((res) => {
            const map = new Map<string, string>();
            for (const p of res.data ?? ([] as PromptTemplate[])) {
              map.set(p.id, p.template);
            }
            return map;
          })
          .catch(() => new Map<string, string>())
      : Promise.resolve(new Map<string, string>());

    try {
      const [next, prompts] = await Promise.all([
        getFanoutCluster(brandId, slug),
        promptsPromise,
      ]);
      setDetail(next);
      setPromptMap(prompts);
    } catch (err) {
      if ((err as { status?: number }).status === 404) {
        setNotFound(true);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [brandId, slug, selectedBrand?.tenant_id]);

  useEffect(() => {
    void load();
  }, [load]);

  const engines = useMemo(() => {
    if (!detail) return [] as string[];
    const set = new Set<string>();
    for (const m of detail.members) {
      for (const e of m.engines_observed) set.add(e);
    }
    return Array.from(set).sort();
  }, [detail]);

  const filteredMembers = useMemo(() => {
    if (!detail) return [] as FanoutQueryMember[];
    let rows = detail.members.slice();
    if (confidenceFilter) {
      rows = rows.filter((m) => m.source_confidence === confidenceFilter);
    }
    if (engineFilter) {
      rows = rows.filter((m) => m.engines_observed.includes(engineFilter));
    }
    rows.sort((a, b) => b.observation_count - a.observation_count);
    return rows;
  }, [detail, confidenceFilter, engineFilter]);

  // Phase 9 Session E.2 (gap XC-R2-1): outer spinner while brand list resolves.
  if (brandsLoading) {
    return (
      <div
        data-testid="brands-loading"
        className="hub-page--reading flex flex-col items-center justify-center py-16 text-slate-500"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  // Breadcrumb leaf — falls back to the slug while the cluster is still
  // loading or when the brand isn't selected, then resolves to the
  // semantic_label once the detail payload arrives.
  const crumbLeaf = detail?.semantic_label ?? slug ?? 'Cluster';
  const crumbs = (
    <Breadcrumbs
      items={[
        { label: 'Fan-out', to: '/fanout' },
        { label: crumbLeaf },
      ]}
    />
  );

  if (!selectedBrand) {
    return (
      <div
        data-testid="fanout-cluster-detail"
        className="hub-page--reading space-y-4"
      >
        {crumbs}
        <EmptyState
          title="No brand selected"
          description="Select a brand to view clusters."
        />
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div
        data-testid="fanout-cluster-detail"
        className="hub-page--reading space-y-4"
      >
        {crumbs}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner  />
          Loading cluster…
        </div>
      </div>
    );
  }

  if (notFound && !detail) {
    return (
      <div
        data-testid="fanout-cluster-detail"
        className="hub-page--reading space-y-4"
      >
        {crumbs}
        <div
          data-testid="cluster-wrong-brand"
          className="rounded-lg border border-slate-200 bg-white p-8 text-center"
        >
          <Icon name="bx-sitemap" className="mx-auto text-slate-300" />
          <h2 className="mt-3 text-base font-semibold text-slate-900">
            Cluster not in {selectedBrand.name}
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            This cluster isn't part of {selectedBrand.name} — it may belong to a
            different brand. Switch brand to view it.
          </p>
        </div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div
        data-testid="fanout-cluster-detail"
        className="hub-page--reading space-y-4"
      >
        {crumbs}
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div
      data-testid="fanout-cluster-detail"
      className="hub-page--reading space-y-5"
    >
      {crumbs}

      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-sitemap" className="text-violet-600" />
          {detail.semantic_label}
        </h1>
        {detail.description && (
          <p className="mt-1 text-sm text-slate-600">{detail.description}</p>
        )}
        <p className="mt-1 font-mono text-xs text-slate-400">{detail.slug}</p>
      </header>

      {/* Metadata card */}
      <section className="hub-section">
        <div className="hub-section__head">
          <h2>Cluster metadata</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <MetaTile label="Queries" value={detail.query_count.toLocaleString()} />
          <MetaTile
            label="Primary sub-query type"
            value={detail.primary_sub_query_type || '—'}
          />
          <MetaTile
            label="Last recomputed"
            value={
              detail.last_recomputed_at
                ? formatRelative(detail.last_recomputed_at)
                : '—'
            }
          />
          <MetaTile
            label="Created"
            value={new Date(detail.created_at).toLocaleDateString()}
          />
        </div>

        {Object.keys(detail.engine_coverage).length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-xs font-medium text-slate-500">
              Engine coverage
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-5">
              {Object.entries(detail.engine_coverage).map(([engine, count]) => (
                <div
                  key={engine}
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-center"
                >
                  <div className="text-base font-semibold text-slate-900">
                    {count}
                  </div>
                  <div className="text-xs text-slate-500">{engine}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Members */}
      <section className="hub-section">
        <div className="hub-section__head">
          <h2 className="text-base font-semibold text-slate-900">
            Members ({filteredMembers.length} of {detail.members.length})
          </h2>
          <div className="flex flex-wrap gap-2">
            <select
              data-testid="cluster-filter-confidence"
              value={confidenceFilter}
              onChange={(e) =>
                setConfidenceFilter(e.target.value as FanoutSourceConfidence | '')
              }
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            >
              <option value="">all confidence</option>
              {CONFIDENCE_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              data-testid="cluster-filter-engine"
              value={engineFilter}
              onChange={(e) => setEngineFilter(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            >
              <option value="">all engines</option>
              {engines.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            No members match the current filters.
          </p>
        ) : (
          <ul
            data-testid="cluster-members-list"
            className="divide-y divide-slate-100"
          >
            {filteredMembers.map((m) => (
              <li
                key={m.id}
                data-testid={`cluster-member-${m.id}`}
                className="py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900">{m.query_text}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span
                        className={`rounded-full px-2 py-0.5 font-medium ${CONFIDENCE_STYLES[m.source_confidence]}`}
                      >
                        {m.source_confidence}
                      </span>
                      <span>{m.observation_count}× observed</span>
                      <span className="text-slate-400">·</span>
                      <span>{m.sub_query_type}</span>
                      <span className="text-slate-400">·</span>
                      <span>last {formatRelative(m.last_seen)}</span>
                    </div>
                    {m.engines_observed.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {m.engines_observed.map((e) => (
                          <span
                            key={e}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    )}
                    <SourcePromptLine member={m} promptMap={promptMap} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Source-prompt provenance for a member row. Observed members render nothing
 * (they already show the observation count + engine chip). Synthesized members
 * (`inferred_*`) show the seed prompt's template text, resolved via promptMap,
 * with the full text as a tooltip. Missing/unresolved id → "(unavailable)".
 */
function SourcePromptLine({
  member,
  promptMap,
}: {
  member: FanoutQueryMember;
  promptMap: Map<string, string>;
}) {
  if (!member.source_confidence.startsWith('inferred_')) return null;

  const ids = extractSeedPromptIds(member);
  const firstId = ids[0];
  const template = firstId ? promptMap.get(firstId) : undefined;

  if (!firstId || template === undefined) {
    return (
      <div className="mt-1.5">
        <span
          data-testid="cluster-member-source-prompt"
          title="Seed prompt not found"
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500"
        >
          <Icon name="bx-message-dots" className="h-3 w-3" />
          source prompt unavailable
        </span>
      </div>
    );
  }

  const extra = ids.length > 1 ? ` (+${ids.length - 1} more)` : '';

  return (
    <div className="mt-1.5">
      <span
        data-testid="cluster-member-source-prompt"
        title={template}
        className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
      >
        <Icon name="bx-message-dots" className="h-3 w-3" />
        from prompt: “{truncate(template, 60)}”{extra}
      </span>
    </div>
  );
}

function MetaTile({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
